import { Box, Button, Grid, useTheme } from '@material-ui/core';
import AddIcon from '@material-ui/icons/AddCircle';
import { useEnvironmentsContext, WbAlert } from '@agilelab/plugin-wb-platform';
import {
  getTemplate,
  getUiOptions,
  ObjectFieldTemplateProps,
  canExpand,
} from '@rjsf/utils';
import { isHidden } from '../../utils';

interface DefaultObjectFieldTemplateProperties {
  displayTitle: boolean;
}

function parseDefaultObjectFieldTemplateProperties(
  props: ObjectFieldTemplateProps,
): DefaultObjectFieldTemplateProperties {
  const inputDisplayTitle = props?.uiSchema?.['ui:options']?.displayTitle;

  const displayTitle: boolean =
    typeof inputDisplayTitle === 'boolean' ? inputDisplayTitle : true;
  return {
    displayTitle,
  };
}

export const DefaultObjectFieldTemplate = (props: ObjectFieldTemplateProps) => {
  const {
    description,
    title,
    properties,
    required,
    disabled,
    readonly,
    uiSchema,
    idSchema,
    schema,
    formData,
    registry,
    onAddClick,
  } = props;
  const { displayTitle } = parseDefaultObjectFieldTemplateProperties(props);
  const { environmentAccess } = useEnvironmentsContext();
  const theme = useTheme();
  const uiOptions = getUiOptions(uiSchema);
  const TitleField = getTemplate('TitleFieldTemplate', registry, uiOptions);
  const DescriptionField = getTemplate(
    'DescriptionFieldTemplate',
    registry,
    uiOptions,
  );
  const environmentParameterSchema = schema as typeof schema & {
    environmentParameters?: boolean;
    stepEnvironment?: string;
  };
  const stepEnvironment = environmentParameterSchema.stepEnvironment;
  const isInaccessibleEnvironment =
    environmentParameterSchema.environmentParameters === true &&
    !!stepEnvironment &&
    (environmentAccess.status === 'empty' ||
      (environmentAccess.status === 'ready' &&
        !environmentAccess.environments.some(
          environment => environment.name === stepEnvironment,
        )));

  return (
    <Box style={{ display: isHidden(uiSchema) ? 'none' : undefined }}>
      {displayTitle && (
        <Box margin={`${theme.spacing(1)}px 0px`}>
          {(uiSchema?.['ui:title'] || title) && (
            <TitleField
              id={`${idSchema.$id}-title`}
              title={title}
              required={required}
              schema={schema}
              registry={registry}
            />
          )}
          {description && (
            <DescriptionField
              id={`${idSchema.$id}-description`}
              description={description}
              schema={schema}
              registry={registry}
            />
          )}
        </Box>
      )}
      {isInaccessibleEnvironment && (
        <WbAlert
          variant="collapsible"
          message={{
            type: 'warning',
            title: `You don't have access to ${stepEnvironment}`,
            content:
              'You can still complete the fields below. For required fields, we strongly recommend working with someone who has access to this environment. Optional fields may remain blank if their values are unknown. Any values you enter remain draft data and take effect only after review and deployment by an authorized person.',
          }}
        />
      )}
      <Grid container spacing={2} style={{ marginTop: '10px' }}>
        {properties.map((element, index) =>
          // Remove the <Grid> if the inner element is hidden as the <Grid>
          // itself would otherwise still take up space.
          element.hidden ? (
            element.content
          ) : (
            <Grid item xs={12} key={index} style={{ marginBottom: '10px' }}>
              {element.content}
            </Grid>
          ),
        )}
        {canExpand(schema, uiSchema, formData) && (
          <Grid container justifyContent="flex-end">
            <Grid item>
              <Button
                className="object-property-expand"
                onClick={onAddClick(schema)}
                disabled={disabled || readonly}
                color="secondary"
              >
                <AddIcon /> Add Item
              </Button>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
